import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  User,
  UserCredential,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';

import { auth, db } from '../firebaseClient';

export const DEFAULT_TOKEN_BALANCE = 100;

interface AuthContextValue {
  user: User | null;
  initializing: boolean;
  signIn: (email: string, password: string) => Promise<UserCredential>;
  register: (email: string, password: string, displayName?: string) => Promise<UserCredential>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const ensureUserDocument = async (user: User) => {
  const userRef = doc(db, 'users', user.uid);
  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) {
    await setDoc(userRef, {
      email: user.email ?? '',
      displayName: user.displayName ?? '',
      tokens: DEFAULT_TOKEN_BALANCE,
      createdAt: serverTimestamp(),
    });
    return;
  }

  await setDoc(
    userRef,
    {
      email: user.email ?? '',
      displayName: user.displayName ?? '',
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      try {
        if (currentUser) {
          await ensureUserDocument(currentUser);
        }
      } catch (error) {
        console.error('[AuthContext] Failed to ensure user document', error);
      } finally {
        setUser(currentUser);
        setInitializing(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const contextValue = useMemo<AuthContextValue>(() => {
    const signIn = (email: string, password: string) => {
      return signInWithEmailAndPassword(auth, email, password);
    };

    const register = async (email: string, password: string, displayName?: string) => {
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      const fallbackDisplayName = email.includes('@') ? email.split('@')[0] : email;
      const finalDisplayName = (displayName ?? fallbackDisplayName).trim();

      if (finalDisplayName) {
        await updateProfile(credential.user, { displayName: finalDisplayName });
      }

      return credential;
    };

    const signOut = async () => {
      await firebaseSignOut(auth);
    };

    const sendReset = (email: string) => {
      return sendPasswordResetEmail(auth, email);
    };

    return {
      user,
      initializing,
      signIn,
      register,
      signOut,
      sendPasswordReset: sendReset,
    };
  }, [user, initializing]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
